import { TrophyIcon } from 'lucide-react';

import icQuestion from '@/assets/ci_question-circle.svg';
import icGroup from '@/assets/ic_grupo_personas.svg';
import { Button, Modal, NavBar, SliderOver } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTapTourney from '@/hooks/useTapTourney';

interface InfoSectionProps {
  icon: JSX.Element | string;
  title: string;
  children: React.ReactNode;
}

interface InfoButtonProps {
  hideButton: boolean;
  handleIsOpenInfo: (boolean: boolean) => void;
}

const InfoSection = ({ icon, title, children }: InfoSectionProps) => (
  <div className="bg-white rounded-lg flex flex-col m-8 p-4 md:m-0 md:p-0">
    <div className="flex items-center space-x-2 mt-6">
      <div className="bg-[#E3EAFF] rounded-full w-8 h-8 flex items-center justify-center">
        {typeof icon === 'string' ? (
          <img alt={title} className="w-5 h-5 text-gdt-blue-light" src={icon} />
        ) : (
          icon
        )}
      </div>
      <p className="font-bold text-[16px] text-[#2855C6]">{title}</p>
    </div>
    <div className="py-4">{children}</div>
  </div>
);

const InfoButton = ({ hideButton, handleIsOpenInfo }: InfoButtonProps) => {
  const { isDesktop } = useIsDesktop();
  const { tapTourneyData } = useTapTourney();

  const sections = [
    {
      icon: icGroup,
      title: '¿Qué son los Torneos de Amigos por Premios?',
      content: (
        <p className="text-[14px]">
          <span className="font-bold">
            Son mini torneos de 20 participantes con premios en efectivo
          </span>{' '}
          para el campeón de cada uno. Hay distintos tipos de Torneos y para
          participar de cada uno deberás ingresar un pin especial.
        </p>
      ),
    },
    {
      icon: <TrophyIcon className="text-[#2855C6]" size={20} />,
      title: 'Premios',
      content: (
        <span className="text-[14px] font-semibold">
          Hay torneos con $75.000 para el campeón, otros con $150.000 para el
          mejor y un tercer grupo con $250.000 para el ganador.
        </span>
      ),
    },
    {
      icon: icQuestion,
      title: '¿Cómo se participa?',
      content: (
        <ul className="text-[14px] list-disc pl-5">
          <li>
            Elegí el torneo en el que querés anotarte: Accedé con tu Pin
            especial. Recordá que hay un tipo de pin por cada tipo de torneo.
          </li>
          <li>Jugás con tu equipo de Primera.</li>
          <li>
            Arrancan todos en la misma fecha. La competencia va hasta el final
            de Gran DT.
          </li>
        </ul>
      ),
    },
  ];

  return (
    <>
      {!hideButton && (
        <Button
          className="pr-4 py-2  text-gdt-blue font-bold"
          href="#"
          type="link"
          variant="text"
          onClick={() => handleIsOpenInfo(true)}
        >
          Más información y premios
        </Button>
      )}

      {isDesktop ? (
        <Modal
          controlledOnClose={() => handleIsOpenInfo(false)}
          isControlledOpen={tapTourneyData?.isOpenInfo ?? false}
          render={() => (
            <>
              {sections.map((section, index) => (
                <InfoSection
                  key={index}
                  icon={section.icon}
                  title={section.title}
                >
                  {section.content}
                </InfoSection>
              ))}
            </>
          )}
          variant="controlled"
        />
      ) : (
        <SliderOver open={tapTourneyData?.isOpenInfo ?? false}>
          <NavBar
            mobile={{
              leftContent: { backAction: () => handleIsOpenInfo(false) },
              centerContent: { title: 'Torneos Por Premios' },
            }}
          >
            <div className="bg-gdt-gray-7 h-[calc(100vh-51px)] overflow-y-auto">
              {sections.map((section, index) => (
                <InfoSection
                  key={index}
                  icon={section.icon}
                  title={section.title}
                >
                  {section.content}
                </InfoSection>
              ))}
            </div>
          </NavBar>
        </SliderOver>
      )}
    </>
  );
};

export default InfoButton;
